import { AxiosError } from 'axios';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { projectFluxControlRoute, projetosPath } from 'library/Routes';
import { CooperativaDTO } from 'models/entidades';
import { EscolaDTO } from 'models/escola';
import {
  DocumentoProjeto,
  EtapaEducacaoDTO,
  EtapaProjeto,
  getEtapaProjetoByIndex,
  indexOfEtapa,
  ModalidadeDTO,
  PrincipioValor,
  PrincipioValorDTO,
  ProjetoType,
  RevisaoDTO,
  StatusEtapa,
} from 'models/projeto';
import { AssessorPedagogicoDTO, EducadorDTO } from 'models/users';
import {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getCooperativasOptions } from 'services/entidades';
import { getEscolasOptions } from 'services/escola';
import {
  getEtapas,
  getModalidades,
  getPrincipiosValores,
  getProjeto,
  getStatusEtapa,
  postProjeto,
  putAvaliacao,
  putProjeto,
} from 'services/projeto';
import { getAssessoresByCoop, getEducadoresByCoop } from 'services/users';
import { closeModal, openModal } from 'store/modules/ModalActions';
import { PUFVProfessorPermissions } from 'store/modules/permissions/PUFVProfessor';
import { PUFVProfessorGestorPermissions } from 'store/modules/permissions/PUFVProfessorGestor';
import languageValues from 'utils/LanguageUtils';
import { getUniqueKey } from 'utils/NumberUtils';
import { etapasConfig, getFlgInfantil } from 'utils/projeto';
import SnackbarUtils from 'utils/SnackbarUtils';
import ProjetosReducer, {
  ProjetosActionType,
  ProjetosState,
} from './ProjetosReducer';
import ProjetosValidator, { ProjetosValidatorType } from './ProjetosValidator';

const STORAGE_ESCOLA = 'ed-last-escola';
const STORAGE_COOP = 'ap-last-coop';

export type DropdownOption = { label: string; value: string | number };

export type Option<T = any> = {
  loading: boolean;
  options: T[];
};

export type Options = {
  instituicao: Option<EscolaDTO>;
  cooperativa: Option<CooperativaDTO>;
  assessores: Option<AssessorPedagogicoDTO>;
  educadores: Option<EducadorDTO>;
  etapasEducacao: Option<EtapaEducacaoDTO>;
  modalidades: Option<ModalidadeDTO>;
  principiosValores: Option<PrincipioValorDTO>;
};

const PROJETO_INITIAL_STATE: Partial<ProjetoType> = {
  flgNovoTemplate: true,
  flgInfantil: false,
  idPrincipiosValores: [PrincipioValor.Cooperacao, PrincipioValor.Cidadania],
  avaliacoes: Object.values(EtapaProjeto).map<RevisaoDTO>((_, index) => {
    return { etapa: index, status: StatusEtapa.SemPreenchimento };
  }),
};

const INITIAL_STATE: ProjetosState = {
  loading: true,
  projeto: PROJETO_INITIAL_STATE,
  etapa: EtapaProjeto.infosIniciais,
  etapasLoading: {},
  statusEtapas: {},
  options: {
    instituicao: { loading: false, options: [] },
    cooperativa: { loading: false, options: [] },
    assessores: { loading: false, options: [] },
    educadores: { loading: false, options: [] },
    etapasEducacao: { loading: false, options: [] },
    modalidades: { loading: false, options: [] },
    principiosValores: { loading: false, options: [] },
  },
  dirty: false,
  sent: false,
  disabledFields: false,
};

export const ProjetosContext = createContext<ProjetosContextType>({
  state: INITIAL_STATE,
  dispatch: () => { },
  validator: undefined,
  carregar: Promise.reject,
  changeEtapa: () => { },
  saveEtapa: Promise.reject,
  mergeAndSaveDocs: Promise.reject,
  loadInstituicoes: Promise.reject,
  loadCooperativas: Promise.reject,
  loadEducadores: Promise.reject,
  loadAssessores: Promise.reject,
  loadEtapasEducacao: Promise.reject,
  loadPrincipiosValores: Promise.reject,
  loadModalidades: Promise.reject,
  avaliarEtapa: Promise.reject,
});
export interface ProjetosContextType {
  state: ProjetosState;
  dispatch: Dispatch<ProjetosActionType>;
  validator?: ProjetosValidatorType;
  carregar: (
    id: string,
    etapa: EtapaProjeto
  ) => Promise<ProjetoType | undefined>;
  changeEtapa: (
    etapa: EtapaProjeto,
    options?: { id?: number; ignoreDirty?: boolean }
  ) => void;
  saveEtapa: (next?: EtapaProjeto) => Promise<number | undefined>;
  mergeAndSaveDocs: (docs: DocumentoProjeto[]) => Promise<DocumentoProjeto[]>;
  loadInstituicoes: () => Promise<EscolaDTO[] | undefined>;
  loadCooperativas: () => Promise<CooperativaDTO[] | undefined>;
  loadEtapasEducacao: () => Promise<EtapaEducacaoDTO[] | undefined>;
  loadAssessores: (
    coop?: string | null
  ) => Promise<AssessorPedagogicoDTO[] | undefined>;
  loadEducadores: (
    idEscola?: number | null | undefined
  ) => Promise<{ data: any; educadoresNecessarios: string[] } | undefined>;
  loadPrincipiosValores: () => Promise<PrincipioValorDTO[] | undefined>;
  loadModalidades: () => Promise<ModalidadeDTO[] | undefined>;
  avaliarEtapa: (opt: {
    comentario?: string;
    status: StatusEtapa;
    etapa: EtapaProjeto;
    dataCadastro: string;
  }) => Promise<void>;
}

export default function ProjetosProvider({
  children,
}: {
  children: ReactNode;
}) {
  const history = useHistory();
  const [state, dispatch] = useReducer(ProjetosReducer, INITIAL_STATE);

  const validator = ProjetosValidator(state.projeto);
  const d = useDispatch();
  const auth = useSelector((state: any) => state.auth);
  const checkpointEtapa = useRef<Partial<ProjetoType>>();
  const storageCurrentEtapa = useCallback(
    (etapa: EtapaProjeto, projeto: Partial<ProjetoType>) => {
      checkpointEtapa.current = Object.keys(etapasConfig[etapa].fields).reduce(
        (ac, at) => {
          return { ...ac, [at]: projeto[at as keyof ProjetoType] };
        },
        {} as Partial<ProjetoType>
      );
    },
    []
  );

  useEffect(() => {
    storageCurrentEtapa(state.etapa, state.projeto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.etapa]);

  const carregar = useCallback(
    async (id: string | number, etapa: EtapaProjeto, silent?: boolean) => {
      if (!state.loading && !silent) {
        dispatch({ type: 'setContratoLoading', payload: true });
      }
      try {
        const { data } = await getProjeto(id);
        const flgInfantil = getFlgInfantil(data?.idEtapasEnsino ?? []);

        dispatch({
          type: 'setProjeto',
          payload: { ...data, flgInfantil, dirty: false },
        });
        // Esta condição está correta
        if (id && data.flgNovoTemplate !== false) {
          storageCurrentEtapa(etapa, data);
          const allReqs = await Promise.all(
            Object.values(EtapaProjeto).map((_, index) => {
              return getStatusEtapa(id, index);
            })
          );
          dispatch({
            type: 'setStatusEtapa',
            payload: allReqs.reduce((ac, at, index) => {
              return {
                ...ac,
                [getEtapaProjetoByIndex(index)]: at.data,
              };
            }, {}),
          });
          if (
            !auth.userPermissions.projetos.editFinished &&
            data.avaliacoes.every((av) => {
              return (
                av.etapa === indexOfEtapa(EtapaProjeto.revisao) ||
                av.status === StatusEtapa.Aprovada
              );
            })
          ) {
            dispatch({
              type: 'setDisabled',
              payload: true,
            });
          }
        }
        return data;
      } catch (error) {
        SnackbarUtils.error(
          (error as AxiosError)?.response?.data?.message ??
          languageValues.toasts.error.loadProjeto
        );
        throw error;
      } finally {
        dispatch({ type: 'setContratoLoading', payload: false });
      }
    },
    [
      auth.userPermissions.projetos.editFinished,
      state.loading,
      storageCurrentEtapa,
    ]
  );

  const goToEtapa = useCallback(
    (step: EtapaProjeto, id?: number) => {
      history.push(
        projectFluxControlRoute.getURLWithParams({
          id,
          step,
        })
      );
    },
    [history]
  );

  const changeEtapa = useCallback(
    (
      toEtapa: EtapaProjeto,
      options?: { ignoreDirty?: boolean; id?: number }
    ) => {
      if (!(validator && validator.etapaIsDisabled[toEtapa])) {
        if (
          !options?.ignoreDirty &&
          state.dirty &&
          toEtapa !== state.etapa
        ) {
          d(
            openModal(ConfirmModal, {
              title: languageValues.modals.confirmChangeEtapa.title,
              content: languageValues.modals.confirmChangeEtapa.text,
              confirmCallback: () => {
                d(closeModal());
                dispatch({
                  type: 'setProjeto',
                  payload: { ...(checkpointEtapa?.current || {}) },
                });
                goToEtapa(toEtapa, options?.id ?? state.projeto.id);
              },
              cancelCallback: () => {
                d(closeModal());
              },
              confirmLabel: languageValues.actions.confirm,
              cancelLabel: languageValues.actions.cancel,
            })
          );
        } else {
          goToEtapa(toEtapa, options?.id ?? state.projeto.id);
        }
      }
    },
    [d, goToEtapa, state.dirty, state.etapa, state.projeto.id, validator]
  );

  const getCurrentStatus = useCallback(
    (etapa: EtapaProjeto) => {
      if (
        etapasConfig[etapa].needValidation === false &&
        validator.etapaIsCompleted[etapa]
      ) {
        return StatusEtapa.Aprovada;
      } else if (validator.etapaIsCompleted[etapa]) {
        return StatusEtapa.AguardandoAprovacao;
      }
      return StatusEtapa.SemPreenchimento;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validator.etapaIsCompleted, state.projeto]
  );

  const saveEtapa = useCallback(
    async (nextEtapa?: EtapaProjeto, options?: { hideToasts: boolean }) => {
      const setLoadingProject = (loading: any) => dispatch({ type: 'setEtapaLoading', payload: { [state.etapa]: loading } });
      const setSent = (sent: any) => dispatch({ type: 'setSent', payload: sent });
      const setProjeto = (id: number, avaliacoes: any) => dispatch({ type: 'setProjeto', payload: { id, dirty: false, avaliacoes } });
      let confirmExit = false;
      const isProfessor = [PUFVProfessorGestorPermissions.id, PUFVProfessorPermissions.id].includes(auth.userData.perfil);
      const isNotInProject = !state.projeto.cpfsProfessores?.includes(auth.userData.cpf);

      if (isProfessor && isNotInProject) {
        confirmExit = await new Promise<boolean>((res) => {
          const modalKey = getUniqueKey();
          d(openModal(ConfirmModal, {
            modalKey,
            title: languageValues.modals.assessorOuProfessorSaindo.title,
            content: languageValues.modals.assessorOuProfessorSaindo.text,
            confirmCallback: () => { d(closeModal(modalKey)); res(true); },
            confirmLabel: languageValues.actions.confirm,
            cancelCallback: () => { d(closeModal(modalKey)); res(false); },
            cancelLabel: languageValues.actions.cancel,
          }));
        });
        if (!confirmExit) return;
      }
      if (state.disabledFields) {
        if (nextEtapa && !validator.etapaIsDisabled[nextEtapa]) {
          goToEtapa(nextEtapa, state.projeto.id);
        }
        return;
      }

      setSent(true);
      if (!validator.etapaIsCompleted[state.etapa]) throw Error('Operação Proibida');
      setLoadingProject(true);

      try {
        const indexOfCurrentEtapa = indexOfEtapa(state.etapa);
        const avaliacoes = state.projeto.avaliacoes
          ?.filter(av => av.etapa !== indexOfCurrentEtapa)
          .concat({ etapa: indexOfCurrentEtapa, status: getCurrentStatus(state.etapa) });
        let id = state.projeto.id;
        const isEditing = Boolean(id);
        if (!isEditing && state.etapa === EtapaProjeto.infosIniciais) {
          const { data } = await postProjeto({ ...state.projeto, avaliacoes });
          id = data.id;
        } else {
          await putProjeto({ ...state.projeto, avaliacoes });
        }
        if (confirmExit) {

          history.push(projetosPath);
          return;
        }
        if (id) {
          await carregar(id, state.etapa, true);
          goToEtapa(nextEtapa ?? state.etapa, id);
          setProjeto(id, avaliacoes);
        }
        if (!options?.hideToasts) {
          SnackbarUtils.success(isEditing ? languageValues.toasts.success.saveEtapa : languageValues.toasts.success.createProjeto);
        }
        return id;
      } catch (error) {
        SnackbarUtils.error((error as AxiosError)?.response?.data?.message ?? languageValues.toasts.error.saveEtapa);
        throw error;
      } finally {
        if (!confirmExit) setLoadingProject(false);
      }
    },
    [
      auth.userData.cpf,
      auth.userData.perfil,
      carregar,
      d,
      getCurrentStatus,
      goToEtapa,
      history,
      state.disabledFields,
      state.etapa,
      state.projeto,
      validator.etapaIsCompleted,
      validator.etapaIsDisabled,
    ]
  );

  const mergeAndSaveDocs = useCallback(
    async (documentos?: DocumentoProjeto[]) => {
      const indexOfCurrentEtapa = indexOfEtapa(state.etapa);
      try {
        dispatch({
          type: 'setEtapaLoading',
          payload: { [state.etapa]: true },
        });
        const mergedDocs =
          state.projeto.documentos
            ?.filter((dParam) => dParam.etapa !== indexOfCurrentEtapa)
            .concat(documentos ?? []) ?? [];
        const avaliacoes = state.projeto.avaliacoes
          ?.filter((av) => {
            return av.etapa !== indexOfCurrentEtapa;
          })
          .concat({
            etapa: indexOfCurrentEtapa,
            status: getCurrentStatus(state.etapa),
          });
        await putProjeto({
          id: state.projeto.id,
          avaliacoes,
          documentos: mergedDocs,
        });
        dispatch({
          type: 'setProjeto',
          payload: { documentos: mergedDocs, dirty: state.dirty },
        });
        return mergedDocs;
      } catch (err) {
        SnackbarUtils.error(languageValues.toasts.error.saveFiles);
        throw err;
      } finally {
        dispatch({
          type: 'setEtapaLoading',
          payload: { [state.etapa]: false },
        });
      }
    },
    [
      getCurrentStatus,
      state.dirty,
      state.etapa,
      state.projeto.avaliacoes,
      state.projeto.documentos,
      state.projeto.id,
    ]
  );

  const loadInstituicoes = useCallback(async () => {
    if (!state.options.instituicao.options.length) {
      dispatch({
        type: 'setOptionsLoading',
        payload: { instituicao: true },
      });
      try {
        const { data } = await getEscolasOptions(state.projeto.idEscola);
        if (data?.length) {
          dispatch({
            type: 'setOptions',
            payload: {
              instituicao: data,
            },
          });
        }
        return data;
      } catch (err) {
        SnackbarUtils.error(languageValues.toasts.error.loadInstituicoes);
        throw err;
      } finally {
        dispatch({
          type: 'setOptionsLoading',
          payload: { instituicao: false },
        });
      }
    }
  }, [state.options.instituicao.options.length, state.projeto.idEscola]);

  const loadCooperativas = useCallback(async () => {
    if (
      !state.options.cooperativa.loading &&
      !state.options.cooperativa.options.length
    ) {
      dispatch({
        type: 'setOptionsLoading',
        payload: { cooperativa: true },
      });
      try {
        const { data } = await getCooperativasOptions();
        dispatch({
          type: 'setOptions',
          payload: {
            cooperativa: data,
          },
        });
        return data;
      } catch (err) {
        SnackbarUtils.error(languageValues.toasts.error.loadCoops);
        throw err;
      } finally {
        dispatch({
          type: 'setOptionsLoading',
          payload: { cooperativa: false },
        });
      }
    }
  }, [
    dispatch,
    state.options.cooperativa.loading,
    state.options.cooperativa.options.length,
  ]);

  const loadModalidades = useCallback(async () => {
    if (
      !state.options.modalidades.loading &&
      !state.options.modalidades.options.length
    ) {
      dispatch({
        type: 'setOptionsLoading',
        payload: { modalidades: true },
      });
      try {
        const { data } = await getModalidades();
        dispatch({
          type: 'setOptions',
          payload: {
            modalidades: data,
          },
        });
        return data;
      } catch (err) {
        SnackbarUtils.error(languageValues.toasts.error.loadModalidades);
        throw err;
      } finally {
        dispatch({
          type: 'setOptionsLoading',
          payload: { modalidades: false },
        });
      }
    }
  }, [
    dispatch,
    state.options.modalidades.loading,
    state.options.modalidades.options.length,
  ]);

  const loadPrincipiosValores = useCallback(async () => {
    if (!state.options.principiosValores.options.length) {
      dispatch({
        type: 'setOptionsLoading',
        payload: { principiosValores: true },
      });
      try {
        const { data } = await getPrincipiosValores();
        if (data?.length) {
          dispatch({
            type: 'setOptions',
            payload: {
              principiosValores: data,
            },
          });
        }
        return data;
      } catch (err) {
        SnackbarUtils.error(languageValues.toasts.error.loadPrincipiosValores);
        throw err;
      } finally {
        dispatch({
          type: 'setOptionsLoading',
          payload: { principiosValores: false },
        });
      }
    }
  }, [dispatch, state.options.principiosValores.options.length]);

  const loadEtapasEducacao = useCallback(async () => {
    if (!state.options.etapasEducacao.options.length) {
      dispatch({
        type: 'setOptionsLoading',
        payload: { etapasEducacao: true },
      });
      try {
        const { data } = await getEtapas(
          state.projeto.flgNovoTemplate !== false
        );
        if (data?.length) {
          dispatch({
            type: 'setOptions',
            payload: {
              etapasEducacao: data,
            },
          });
        }
        return data;
      } catch (err) {
        SnackbarUtils.error(languageValues.toasts.error.loadEtapasEducacao);
        throw err;
      } finally {
        dispatch({
          type: 'setOptionsLoading',
          payload: { etapasEducacao: false },
        });
      }
    }
  }, [
    dispatch,
    state.options.etapasEducacao.options.length,
    state.projeto.flgNovoTemplate,
  ]);

  const setLoading = (loading: any) => dispatch({ type: 'setOptionsLoading', payload: { educadores: loading } });
  const setOptions = (options: any) => dispatch({ type: 'setOptions', payload: { educadores: options } });

  const isEscolaStored = (idEscola: number) => {
    const storedIdEscola = sessionStorage.getItem(STORAGE_ESCOLA);
    return idEscola && storedIdEscola && idEscola === parseInt(storedIdEscola) && state.options.educadores.options.length;
  };

  const fetchEducadores = async (idEscola: number) => {
    const escola = state.options.instituicao.options.find(e => e.id === idEscola);
    if (escola ?? state.projeto.codigoCooperativa) {
      const { data } = await getEducadoresByCoop({
        codCoop: escola?.coop ?? state.projeto.codigoCooperativa!,
        educadoresNecessarios: state.projeto.cpfsProfessores ?? [],
        idEscola: idEscola,
      });
      sessionStorage.setItem(STORAGE_ESCOLA, String(idEscola));
      setOptions(data);
      return data;
    } else {
      SnackbarUtils.error(languageValues.toasts.error.loadEducadores);
    }
  };

  const loadEducadores = useCallback(
    async (idEscola?: number | null) => {
      if (typeof idEscola === 'number') {
        setLoading(true);
        try {
          if (isEscolaStored(idEscola)) {
            return;
          }
          if (idEscola) {
            return await fetchEducadores(idEscola);
          } else {
            setOptions([]);
          }
        } catch (err) {
          SnackbarUtils.error(languageValues.toasts.error.loadEducadores);
          throw err;
        } finally {
          setLoading(false);
        }
      } else {
        sessionStorage.removeItem(STORAGE_ESCOLA);
        setOptions([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state.options.educadores.options.length,
      state.options.instituicao.options,
      state.projeto.codigoCooperativa,
      state.projeto.cpfsProfessores,
    ]
  );

  const loadAssessores = useCallback(
    async (coop?: string | null) => {
      if (typeof coop === 'string') {
        dispatch({
          type: 'setOptionsLoading',
          payload: { assessores: true },
        });
        try {
          if (
            coop &&
            coop === sessionStorage.getItem(STORAGE_COOP) &&
            state.options.assessores.options.length
          ) {
            return;
          } else if (coop) {
            const { data } = await getAssessoresByCoop(
              coop,
              state.projeto.cpfsAssessores
            );
            sessionStorage.setItem(STORAGE_COOP, coop);
            dispatch({
              type: 'setOptions',
              payload: {
                assessores: data,
              },
            });
            return data;
          } else {
            dispatch({
              type: 'setOptions',
              payload: {
                assessores: [],
              },
            });
          }
        } catch (err) {
          SnackbarUtils.error(languageValues.toasts.error.loadAssessores);
          throw err;
        } finally {
          dispatch({
            type: 'setOptionsLoading',
            payload: { assessores: false },
          });
        }
      } else {
        sessionStorage.removeItem(STORAGE_COOP);
        dispatch({
          type: 'setOptions',
          payload: {
            assessores: [],
          },
        });
      }
    },
    [state.options.assessores.options.length, state.projeto.cpfsAssessores]
  );

  const avaliarEtapa = useCallback(
    async (opt: {
      comentario?: string;
      status: StatusEtapa;
      etapa: EtapaProjeto;
      dataCadastro: string;
    }) => {
      try {
        const rev = {
          ...opt,
          nomeAssessor: auth.userData.nome,
          ldap: auth.userData.ldap,
          idProjeto: state.projeto.id!,
        };
        await putAvaliacao(rev);
        const historico =
          opt.etapa in state.statusEtapas
            ? [...(state.statusEtapas[opt.etapa]?.historico ?? [])]
            : [];
        const newAv = {
          comentario: opt.comentario,
          status: opt.status,
          etapa: indexOfEtapa(opt.etapa),
          nomeAssessor: auth.userData.nome,
          cpfAssessor: auth.userData.ldap,
          dataCadastro: opt.dataCadastro,
        };
        historico.push(newAv);
        const avaliacoes = state.projeto.avaliacoes
          ?.filter((e) => e.etapa !== indexOfEtapa(opt.etapa))
          .concat(newAv);
        if (
          avaliacoes?.some(
            (av) => av.etapa === indexOfEtapa(EtapaProjeto.revisao)
          )
        )
          dispatch({
            type: 'setStatusEtapa',
            payload: {
              [opt.etapa]: {
                status: opt.status,
                historico,
              },
            },
          });
        dispatch({
          type: 'setProjeto',
          payload: {
            avaliacoes,
            dirty: state.dirty,
          },
        });
      } catch (err) {
        SnackbarUtils.error(languageValues.toasts.error.avaliarEtapa);
        throw err;
      }
    },
    [
      auth.userData.ldap,
      auth.userData.nome,
      state.dirty,
      state.projeto.avaliacoes,
      state.projeto.id,
      state.statusEtapas,
    ]
  );

  return (
    <ProjetosContext.Provider
      value={{
        state,
        dispatch,
        validator,
        carregar,
        changeEtapa,
        saveEtapa,
        mergeAndSaveDocs,
        loadInstituicoes,
        loadCooperativas,
        loadAssessores,
        loadEducadores,
        loadEtapasEducacao,
        loadPrincipiosValores,
        loadModalidades,
        avaliarEtapa,
      }}
    >
      {children}
    </ProjetosContext.Provider>
  );
}
